import * as yup from 'yup'
import { ApplicationFormAnswers } from '../components/ApplicationForm/ApplicationFormContext'
import BaseValidator from './BaseValidator'
import employmentStatusOptions, { employerDetailsRequired } from '../components/Questions/CardlessEmploymentHistoryQuestion/EmploymentStatus/EmploymentStatusOptions'
import { getHistoryDuration } from '../../packages/utilities'

const lengthOfTimeObject = {
  months: yup.number()
    .transform((val) => (Number.isNaN(val) ? undefined : val))
    .max(11, 'Please enter a value between 0 and 11')
    .min(0, 'Please enter a value between 0 and 11'),
  years: yup.number()
    .transform((val) => (Number.isNaN(val) ? undefined : val))
    .max(100, 'Please enter a value between 0 and 100')
    .min(0, 'Please enter a value between 0 and 100')
}

const employmentDetailsObject = {
  jobTitle: yup.string().required('Please enter your job title').max(100),
  companyName: yup.string().required('Please enter the company name').max(100)
}

const employmentDetailsObjectWithoutLocation = {
  jobTitle: employmentDetailsObject.jobTitle,
  companyName: employmentDetailsObject.companyName
}

const EmploymentDetailsValidator = (includeTimeInSituationFields: boolean): BaseValidator => {
  let schema: yup.AnySchema

  if (includeTimeInSituationFields) {
    schema = yup.object().shape({
      ...employmentDetailsObjectWithoutLocation,
      ...lengthOfTimeObject
    }).test(
      'is-optional',
      "Please enter years or months of how long you've worked here",
      ({ months, years }, { createError }) => {
        if (years === undefined && months === undefined) return createError({ path: 'duration', message: 'Please enter years or months of how long you\'ve worked here' })
        return (!(years === undefined && months === undefined))
      }
    )
  } else {
    schema = yup.object().shape({
      ...employmentDetailsObjectWithoutLocation
    })
  }

  return {
    getValidator: () => schema,
    validateState: () => false
  }
}

const LengthOfTimeValidator = (): BaseValidator => {
  const schema = yup.object().shape({ ...lengthOfTimeObject }).test(
    'is-optional',
    "Please enter years or months of how long you've worked here",
    ({ months, years }, { createError }) => {
      if (years === undefined && months === undefined) return createError({ path: 'duration', message: 'Please enter years or months of how long you\'ve worked here' })
      return (!(years === undefined && months === undefined))
    }
  )

  return {
    getValidator: () => schema,
    validateState: () => false
  }
}

const EmploymentHistoryValidator = {
  LengthOfTimeValidator,
  EmploymentDetailsValidator,
  EmploymentSituationValidator: (): BaseValidator => {
    const schema = yup.object().shape({
      employmentSituation: yup.string()
        .oneOf(employmentStatusOptions.map((o) => o.value as string))
        .required()
    })
    return {
      getValidator: () => schema,
      validateState: (state: ApplicationFormAnswers) => (state.employmentHistory?.entries
        ? Object.keys(state.employmentHistory?.entries).every((key: string) => {
          const entry = state.employmentHistory?.entries[key]
          return schema.isValidSync({
            employmentSituation: entry?.employmentSituation?.toLowerCase()
          })
        }) : false)
    }
  },
  EmploymentHistoryLengthValidator: (): BaseValidator => ({
    getValidator: () => yup.object(),
    validateState: (state: ApplicationFormAnswers) => {
      const entries = state.employmentHistory?.entries
      if (!entries) {
        return false
      }
      const historyLength = getHistoryDuration(entries)
      return !!((historyLength.years && historyLength.years >= 2))
    }
  }),
  EmploymentEntryValidator: (): BaseValidator => ({
    getValidator: () => yup.object(),
    validateState: (state: ApplicationFormAnswers) => (
      state.employmentHistory?.entries
        ? Object.keys(state.employmentHistory.entries).every((key: string) => {
          const entry = state.employmentHistory?.entries[key]
          const validateEmploymentDetails = entry?.employmentSituation
            ? employerDetailsRequired.includes(entry.employmentSituation.toLowerCase()) : false
          return validateEmploymentDetails
            ? EmploymentDetailsValidator(true).getValidator().isValidSync({
              jobTitle: entry?.employerDetails?.jobTitle,
              companyName: entry?.employerDetails?.companyName,
              years: entry?.lengthOfTime?.years,
              months: entry?.lengthOfTime?.months
            }) : LengthOfTimeValidator().getValidator().isValidSync({
              years: entry?.lengthOfTime?.years,
              months: entry?.lengthOfTime?.months
            })
        }) : false)
  })
}

export default EmploymentHistoryValidator
